import { S as Swiper } from './swiper-core-Bbvcim4W.js';
import { N as Navigation } from './navigation-DRozqAD1.js';
import { A as A11y } from './a11y-SU9kYSAf.js';
import { M as Manipulation } from './manipulation-BSmgpsI5.js';

class InstagramSlider extends HTMLElement {
  constructor() {
    super();

    this.feedUrl = this.dataset.feedUrl;
    this.numberOfPosts = parseInt(this.dataset.numberOfPosts);

    this.swiper = this.querySelector('.swiper');

    // Call swiper with selected swiper element and options
    this.swiperInstance = new Swiper(this.swiper, {
      modules: [A11y, Manipulation, Navigation],
      threshold: 10,
      roundLengths: true,
      slidesPerView: 1.5,
      spaceBetween: 16,
      watchSlidesProgress: true,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        480: {
          slidesPerView: 2.5,
        },
        768: {
          slidesPerView: 4,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 24,
        },
      },
    });

    this.onSlideChange();
    this.displayInstagramSlider();
    this.handleDesignModeEvents();
  }

  /**
   * Fetch Instagram feed
   * @returns json (feed)
   */
  async fetchInstagramSlider() {
    if (!this.feedUrl) return;
    try {
      const response = await fetch(this.feedUrl);
      const json = await response.json();

      // Catch error status and throw error
      if (json.status === 'error') throw new Error(json.message);

      return json.posts;
    } catch (error) {
      console.error(error);
      return;
    }
  }

  /**
   * Display the Instagram feed
   * @returns void
   */
  async displayInstagramSlider() {
    if (!this.feedUrl) return;
    const instagramPosts = await this.fetchInstagramSlider();

    // If feed is empty, return
    if (!instagramPosts) return;

    // Remove all slides
    this.swiperInstance.removeAllSlides();

    await instagramPosts.forEach((post, i) => {
      // Break if index is higher than number of posts
      if (i > this.numberOfPosts) return;

      // Add the slides
      const slideContent = (() => {
        switch (post.mediaType) {
          case 'IMAGE':
          case 'CAROUSEL_ALBUM':
            return this.buildImagePost(post);
          case 'VIDEO':
            return this.buildVideoPost(post);
          default:
            return '';
        }
      })();

      this.swiperInstance.addSlide(i, slideContent);
    });

    // Handle video autoplay for visible slides
    this.swiperInstance.slides.forEach((slide) => this.handleVideoAutoplay(slide));
  }

  /**
   * Build image node
   * @param {Object} post
   * @returns image node
   */
  buildImagePost(post) {
    const {
      sizes: {
        medium: { mediaUrl, width, height },
      },
      mediaType,
      permalink,
    } = post;

    return `
      <div class="swiper-slide overflow-hidden">
        <a href="${permalink}" target="_blank" rel="noopener noreferrer">
          <figure class="img img--wrapper img--cover instagram-slider__image">
            <img
              src="${mediaUrl}"
              alt="Image from the Instagram of @bronjewelry"
              width="${width}"
              height="${height}"
              loading="lazy"
              title="Image from the Instagram of @bronjewelry"
              crop="center"
              style="aspect-ratio:4/5;"
            >
          </figure>
        </a>
      </div>
    `;
  }

  /**
   * Build video node
   * @param {Object} post
   * @returns video node
   */
  buildVideoPost(post) {
    const { dimensions, mediaType, mediaUrl, permalink } = post;

    // Get media url type (.mp4 etc.)
    let mediaUrlType = mediaUrl.split('?')[0];
    const lastDotInUrl = mediaUrlType.lastIndexOf('.');
    mediaUrlType = mediaUrlType.slice(lastDotInUrl + 1);

    return `
      <div class="swiper-slide">
        <a href="${permalink}" target="_blank" rel="noopener noreferrer">
          <div class="video instagram-slider__video">
            <video
              width="${dimensions.width}"
              height="${dimensions.height}"
              title="${mediaType} from the Instagram of @bronjewelry"
              muted
              loop
            >
              <source src="${mediaUrl}" type="video/${mediaUrlType}">
              Your browser does not support the video tag.
            </video>
          </div>
        </a>
      </div>
    `;
  }

  /**
   * Handle slide changes
   */
  onSlideChange() {
    if (!this.feedUrl) return;
    this.swiperInstance.on('slideChange', () => {
      this.swiperInstance.slides.forEach((slide) => {
        this.handleVideoAutoplay(slide);
      });
    });
  }

  /**
   * Handle video autoplay
   * @param {Node} slide
   * @returns void
   */
  handleVideoAutoplay(slide) {
    const video = slide.querySelector('video');
    if (!video) return;
    slide.classList.contains('swiper-slide-visible') ? video.play() : video.pause();
  }

  /**
   * Handles the theme editor block change/edit event
   * @param {Object} event
   */
  handleBlockSelect({ dataset }) {
    // Check if the slide index is set
    if (!('swiperSlideIndex' in dataset)) return;

    // Set the slide index based on loop settings or not
    let swipeToSlideIndex = parseInt(dataset.swiperSlideIndex) - 1;
    if (this.swiperOptions.loop) swipeToSlideIndex = parseInt(dataset.swiperSlideIndex) + 1;

    // Slide to slide based on the data attribute from the target
    this.swiperInstance.slideTo(swipeToSlideIndex, 1000);
  }

  /**
   * Handle design mode events
   * @returns void
   */
  handleDesignModeEvents() {
    if (!Shopify.designMode) return;

    // Update the swiper when the section event is triggered
    ['shopify:section:load', 'shopify:block:select', 'shopify:block:deselect'].forEach(
      (inputEvent) => {
        window.addEventListener(inputEvent, (event) => {
          this.swiperInstance.update();

          if (this.swiperOptions.navigation) {
            this.swiperInstance.navigation.init();
            this.swiperInstance.navigation.update();
          }

          if (inputEvent === 'shopify:block:select' || inputEvent === 'shopify:block:deselect')
            this.handleBlockSelect(event.target);
        });
      }
    );
  }
}

if (!customElements.get('instagram-slider')) {
  customElements.define('instagram-slider', InstagramSlider);
}
